import { darkColors, Modal, theme } from '@tryhackme/thm-ui-components';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { useGetInfluencersCampaignsQuery } from 'src/features/admin/campaigns/campaigns.slice';
import { Countdown } from 'src/features/campaigns/components/countdown';
import { useActiveCampaign } from 'src/features/campaigns/hooks/use-active-campaign';
import { useCampaignStorage } from 'src/features/campaigns/hooks/use-campaign-storage';
import { ThemeProvider } from 'styled-components';
import { useInfluencerConfigs } from '../../hooks/use-influencer-additional-configs';
import { useInfluencerCampaign } from '../../hooks/use-influencer-campaign';
import { getPathNameWithoutQueryParams } from '../../utils/influencer.formaters';
import { InfluencerModalSvg } from '../modal-content/influencer-modal-svg';
import { StyledContent, StyledCountdownWrapper, StyledMainText, StyledRedirectButton, Styledtag, StyledWrapper } from './influencer-campaign-modal.styles';
export const InfluencerCampaignModal: FC = () => {
  const {
    data: userDataResponse
  } = useGetUserQuery();
  const {
    campaign
  } = useActiveCampaign();
  const {
    isSeen,
    markAsSeen
  } = useCampaignStorage(campaign);
  const {
    data: campaigns
  } = useGetInfluencersCampaignsQuery({
    page: 1,
    limit: 1,
    name: campaign?.name
  });
  const campaignData = campaigns?.data?.docs[0];
  const campaignConfigs = useInfluencerCampaign(campaign?.name, campaignData);
  const {
    pathname
  } = useLocation();
  const [trackEvent] = useTrackEventMutation();
  const isLoggedIn = !!userDataResponse?.data;
  const [open, setOpen] = useState(isLoggedIn && !isSeen);
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setOpen(isLoggedIn && !isSeen);
  }, [isLoggedIn, isSeen]);
  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.focus();
    }
  }, [open]);
  const onOpenChange = (isOpen: boolean) => {
    if (isOpen) return;
    markAsSeen();
    setOpen(false);
  };
  const handleCtaClick = () => {
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'click-influencer-campaign-modal',
      properties: {
        name: campaign?.name,
        page: getPathNameWithoutQueryParams(pathname)
      }
    });
    markAsSeen();
    setOpen(false);
  };
  const handleCloseButtonAction = () => {
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'close-influencer-campaign-modal',
      properties: {
        name: campaign?.name,
        page: getPathNameWithoutQueryParams(pathname)
      }
    });
    markAsSeen();
    setOpen(false);
  };
  const additionalConfigs = useInfluencerConfigs(campaignData);
  if (!campaign || !additionalConfigs || !campaignConfigs) return null;
  const {
    userFriendlyName,
    isExtended,
    shouldIncludeDays,
    percentageOff,
    calculatedEndTime
  } = campaignConfigs;
  const {
    urlToNavigate,
    detailsRedirect,
    icon
  } = additionalConfigs;
  const url = userDataResponse?.data ? detailsRedirect ?? '' : urlToNavigate;
  const modalTitleId = 'influencer-modal-title';
  const modalDescriptionId = 'influencer-modal-description';
  return <ThemeProvider theme={{
    ...theme,
    colors: darkColors
  }} data-sentry-element="ThemeProvider" data-sentry-component="InfluencerCampaignModal" data-sentry-source-file="influencer-campaign-modal.tsx">
      <Modal open={open} onOpenChange={onOpenChange} padding="0" overflowVisible preventCloseOnOutsideClick handleCloseButtonAction={handleCloseButtonAction} data-sentry-element="Modal" data-sentry-source-file="influencer-campaign-modal.tsx">
        <StyledWrapper data-sentry-element="StyledWrapper" data-sentry-source-file="influencer-campaign-modal.tsx">
          <StyledContent ref={contentRef} tabIndex={-1} data-testid="influencer-modal-content" data-sentry-element="StyledContent" data-sentry-source-file="influencer-campaign-modal.tsx">
            {icon && <InfluencerModalSvg icon={icon} width="300" height="237" />}
            <Styledtag data-sentry-element="Styledtag" data-sentry-source-file="influencer-campaign-modal.tsx">{userFriendlyName} viewers exclusive</Styledtag>
            {isExtended ? <div>
                <StyledMainText id={modalTitleId}>
                  FINAL CHANCE TO <span>SAVE {percentageOff}%!</span>
                </StyledMainText>
                <p id={modalDescriptionId}>
                  Extended on popular demand, the <strong>{percentageOff}%</strong> off Jr Penetration Tester
                  Certification is still live - but not for long.
                </p>
              </div> : <div>
                <StyledMainText id={modalTitleId}>
                  <span>{percentageOff}% off</span> Jr Penetration Tester certification!!
                </StyledMainText>
                <p id={modalDescriptionId}>
                  You&apos;ve unlocked a special offer! {userFriendlyName} viewers get{' '}
                  <strong>{percentageOff}% off</strong> Jr Penetration Tester Certification!
                </p>
              </div>}
            {!isExtended && <StyledCountdownWrapper>
                <p>Offer ends in: </p>
                <Countdown includeDays={shouldIncludeDays} dark vertical endTime={calculatedEndTime} />
              </StyledCountdownWrapper>}
            <StyledRedirectButton as={Link} to={url} onClick={handleCtaClick} variant="solid" color="primary" data-sentry-element="StyledRedirectButton" data-sentry-source-file="influencer-campaign-modal.tsx">
              Let&apos;s Go!
            </StyledRedirectButton>
          </StyledContent>
        </StyledWrapper>
      </Modal>
    </ThemeProvider>;
};